import { useEffect, useRef, useState } from "react";
import { Scroll, Sparkles, Loader2, ShieldCheck, ShieldAlert, ToggleLeft, ToggleRight, LogIn } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { api } from "../../lib/api";
import SaveToNotebookButton from "../SaveToNotebookButton";
import ShareMenu from "../share/ShareMenu";
import ShareCard from "../share/ShareCard";
import { toast } from "sonner";

/**
 * ParashaContentDialog — unified modal for the two parasha-card
 * content buttons: "ממקורות חז״ל" (mode="aggadah") and
 * "סיפור לפרשה" (mode="story").
 *
 * Props:
 *   open       — bool
 *   onClose    — () => void
 *   mode       — "aggadah" | "story"
 *   slug       — parasha slug (e.g. "Bereshit")
 *   parashaHe  — Hebrew parasha name (e.g. "בראשית")
 *   lang       — "he" | "en"  (default "he")
 *   adminMode  — boolean — when true, fetches the admin EN endpoint
 *                (returns EN payload + approval status even without
 *                approval) and renders the approve toggle. Used by
 *                /admin/aggadot. Default false.
 *   onApprovalChange — optional (newApproved) => void — fires after
 *                an admin toggles approval so the list can refresh.
 *
 * Behaviour:
 *   • lang="he" (public): existing HE flow — verbatim source + HE
 *     paraphrase + HE lesson. Unchanged from the Feb 28 ship.
 *   • lang="en" (public, regular user):
 *       - If `approved_en === true` (server returned EN payload) →
 *         renders the full English content (title_en / paraphrase_en /
 *         lesson_en for aggadah; story_title_en / story_en /
 *         story_lesson_en for story).
 *       - If the server returned HE payload + `english_pending: true`
 *         → renders the HE content as fallback PLUS a small notice
 *         "English version coming soon — showing Hebrew for now."
 *         (Option A from the spec — never leave the user empty-handed.)
 *   • adminMode=true: bypasses approval; always pulls EN content from
 *     the admin endpoint. Adds an HE/EN tab switcher so the rabbi can
 *     compare, plus an "Approve English version" toggle.
 */
export default function ParashaContentDialog({
  open, onClose, mode, slug, parashaHe,
  lang = "he",
  adminMode = false,
  onApprovalChange,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isStory = mode === "story";
  const isEn = lang === "en";

  // Public payload (response from /parasha/{slug}/{kind}?lang=...)
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorKind, setErrorKind] = useState(null); // null | "missing" | "fetch" | "auth"
  const shareCardRef = useRef(null);

  // Admin-only — the EN admin payload (always EN regardless of approval)
  // and the HE payload (loaded lazily when user flips the tab).
  const [adminEnData, setAdminEnData] = useState(null);
  const [adminHeData, setAdminHeData] = useState(null);
  const [adminTab, setAdminTab] = useState("he"); // "he" | "en"
  const [adminApproved, setAdminApproved] = useState(false);
  const [adminApproving, setAdminApproving] = useState(false);

  useEffect(() => {
    if (!open || !slug || !mode) return;
    let alive = true;
    setData(null);
    setAdminEnData(null);
    setAdminHeData(null);
    setErrorKind(null);
    setLoading(true);
    setAdminApproved(false);

    if (adminMode) {
      // Admin path — always pull EN payload AND the HE one so the
      // rabbi can compare side-by-side via the in-dialog tabs.
      const enKind = isStory ? "story" : "aggadah";
      const enPromise = api.adminAggadotEn(slug, enKind);
      const hePromise = isStory ? api.parashaStory(slug) : api.parashaAggadah(slug);
      Promise.all([enPromise, hePromise.catch(() => null)])
        .then(([enRes, heRes]) => {
          if (!alive) return;
          setAdminEnData(enRes);
          setAdminHeData(heRes);
          setAdminApproved(!!enRes?.approved_en);
          // Default the admin to the EN tab so the review is front-and-center.
          setAdminTab("en");
        })
        .catch((err) => {
          if (!alive) return;
          const status = err?.response?.status;
          if (status === 401 || status === 403) setErrorKind("auth");
          else if (status === 404 || status === 503) setErrorKind("missing");
          else setErrorKind("fetch");
        })
        .finally(() => { if (alive) setLoading(false); });
    } else {
      // Public path — single call with optional lang param.
      const promise = isStory
        ? api.parashaStory(slug, isEn ? "en" : undefined)
        : api.parashaAggadah(slug, isEn ? "en" : undefined);
      promise
        .then((d) => { if (alive) setData(d); })
        .catch((err) => {
          if (!alive) return;
          const status = err?.response?.status;
          // 401/403 → user isn't signed in (or session expired).
          // Show a friendly sign-in CTA rather than the generic
          // "could not load" message, which used to mislead returning
          // visitors into thinking the server was broken.
          if (status === 401 || status === 403) setErrorKind("auth");
          else if (status === 404 || status === 503) setErrorKind("missing");
          else setErrorKind("fetch");
        })
        .finally(() => { if (alive) setLoading(false); });
    }
    return () => { alive = false; };
  }, [open, slug, mode, isEn, adminMode, isStory]);

  const handleApprovalToggle = async () => {
    if (!slug || adminApproving) return;
    setAdminApproving(true);
    const next = !adminApproved;
    try {
      const kind = isStory ? "story" : "aggadah";
      const res = await api.adminAggadotApproveEn(slug, kind, next);
      setAdminApproved(!!res?.approved_en);
      toast.success(res?.approved_en
        ? "English version approved — visible to all users."
        : "English version unpublished — users will see Hebrew with the 'coming soon' notice.");
      onApprovalChange?.(!!res?.approved_en);
    } catch {
      toast.error("Could not update approval. Try again.");
    } finally {
      setAdminApproving(false);
    }
  };

  // ── Render decision ─────────────────────────────────────────
  // Determine which payload to draw FROM and which language to draw IN.
  // adminMode  → uses `adminEnData` for the EN tab and `adminHeData` for the HE tab.
  // public !en → uses `data` directly (HE payload, existing behaviour).
  // public en  → if `english_pending` use `data` HE fields; else use `data` EN fields.
  let drawData = data;
  let drawLang = "he"; // "he" or "en"
  let pendingNote = false; // show the small "English coming soon" note
  if (adminMode) {
    drawData = adminTab === "en" ? adminEnData : adminHeData;
    drawLang = adminTab;
  } else if (isEn && data) {
    if (data.approved_en && data.lang === "en") {
      drawData = data;
      drawLang = "en";
    } else if (data.english_pending) {
      drawData = data;       // HE fields are present in the response
      drawLang = "he";
      pendingNote = true;
    }
  }

  // Header copy. For empty / loading / error states (no payload yet),
  // `drawLang` defaults to "he" because that only flips once `data` is
  // present. Without an `isEn` fallback the dialog header would stay
  // Hebrew even when the UI language is English (e.g. unauthenticated
  // visitors hitting the auth-required state). Honour `isEn` directly
  // in those branches so EN users get an EN header.
  const titleText = (() => {
    if (!drawData) {
      const useEn = isEn || drawLang === "en";
      if (loading) {
        if (useEn) return isStory ? "Composing the story…" : "Loading sources of Chazal…";
        return isStory ? "מחבר את הסיפור…" : "טוען מקורות חז״ל…";
      }
      return useEn
        ? (isStory ? "Shabbat-table story" : "From the Sources of Chazal")
        : (isStory ? "סיפור לשולחן שבת" : "ממקורות חז״ל");
    }
    if (isStory) {
      return drawLang === "en"
        ? (drawData.story_title_en || drawData.story_title_he || "")
        : (drawData.story_title_he || "");
    }
    return drawLang === "en"
      ? (drawData.title_en || drawData.title_he || "")
      : (drawData.title_he || "");
  })();

  const eyebrowText = (() => {
    const useEn = isEn || drawLang === "en";
    if (useEn) {
      const head = isStory ? "Story for the Parasha" : "From the Sources of Chazal";
      return parashaHe ? `${head} · Parashat ${slug || parashaHe}` : head;
    }
    return `${isStory ? "סיפור לפרשה" : "ממקורות חז״ל"}${parashaHe ? ` · פרשת ${parashaHe}` : ""}`;
  })();

  const descriptionText = (() => {
    const useEn = isEn || drawLang === "en";
    if (useEn) {
      return isStory
        ? "A modern English story for the Shabbat table, based on the curated midrash."
        : "From the words of Chazal — original source, paraphrase, and a lesson.";
    }
    return isStory
      ? "סיפור חי לשולחן שבת, מבוסס על אגדת חז״ל ומותאם לעברית של היום."
      : "ממקורות חז״ל — לשון המדרש, פרפראזה ומסר.";
  })();

  // Notebook payload — only built when we have data to save. In EN we
  // compose an English-flavored notebook entry; in HE we keep the
  // original Hebrew shape so existing notebook rendering is untouched.
  const notebookPayload = drawData ? buildNotebookPayload(mode, slug, parashaHe, drawData, drawLang) : null;

  // Direction follows the same EN/HE fallback rule as the header copy
  // but ONLY when the payload hasn't arrived yet — once data exists,
  // `drawLang` is authoritative (it correctly stays "he" for EN users
  // viewing english_pending content, so the Hebrew text stays RTL).
  const dialogDir =
    drawLang === "en" || (!drawData && isEn) ? "ltr" : "rtl";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        dir={dialogDir}
        data-testid={`parasha-${mode}-dialog`}
      >
        <DialogHeader className={drawLang === "en" ? "text-left space-y-2" : "text-right space-y-2"}>
          <div className="flex items-center gap-2 text-gold">
            {isStory ? (
              <Sparkles className="h-4 w-4" strokeWidth={1.5} />
            ) : (
              <Scroll className="h-4 w-4" strokeWidth={1.5} />
            )}
            <span className="font-heading text-xs tracking-widest uppercase">
              {eyebrowText}
            </span>
          </div>
          <DialogTitle
            data-testid={`parasha-${mode}-dialog-title`}
            className="font-heading text-2xl sm:text-3xl text-foreground leading-snug"
          >
            {titleText}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {descriptionText}
          </DialogDescription>
        </DialogHeader>

        {/* Admin bar: HE/EN tab switcher + Approve toggle */}
        {adminMode && !loading && !errorKind && (
          <div
            data-testid={`parasha-${mode}-admin-bar`}
            className="flex flex-wrap items-center justify-between gap-3 -mt-2 mb-2 px-3 py-2 rounded-sm bg-foreground/[0.04] border border-foreground/10"
            dir="rtl"
          >
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">תצוגה:</span>
              <button
                type="button"
                data-testid={`parasha-${mode}-admin-tab-he`}
                onClick={() => setAdminTab("he")}
                className={`text-xs font-heading px-2.5 py-1 rounded-sm transition-colors ${
                  adminTab === "he"
                    ? "bg-gold/15 text-gold border border-gold/40"
                    : "text-muted-foreground hover:text-foreground border border-transparent"
                }`}
              >
                עברית
              </button>
              <button
                type="button"
                data-testid={`parasha-${mode}-admin-tab-en`}
                onClick={() => setAdminTab("en")}
                className={`text-xs font-heading px-2.5 py-1 rounded-sm transition-colors ${
                  adminTab === "en"
                    ? "bg-gold/15 text-gold border border-gold/40"
                    : "text-muted-foreground hover:text-foreground border border-transparent"
                }`}
              >
                English
              </button>
            </div>

            <div className="flex items-center gap-2">
              {adminApproved ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-heading uppercase tracking-widest text-emerald-600">
                  <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.5} />
                  אנגלית מאושרת
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[11px] font-heading uppercase tracking-widest text-muted-foreground">
                  <ShieldAlert className="h-3.5 w-3.5" strokeWidth={1.5} />
                  אנגלית לא מאושרת
                </span>
              )}
              <button
                type="button"
                data-testid={`parasha-${mode}-admin-approve-toggle`}
                onClick={handleApprovalToggle}
                disabled={adminApproving}
                className="inline-flex items-center gap-1.5 text-xs font-heading px-3 py-1 rounded-sm border border-gold/40 hover:border-gold hover:bg-gold/10 transition-colors disabled:opacity-50"
              >
                {adminApproved ? (
                  <ToggleRight className="h-4 w-4 text-gold" strokeWidth={1.5} />
                ) : (
                  <ToggleLeft className="h-4 w-4" strokeWidth={1.5} />
                )}
                {adminApproved ? "בטל אישור" : "אשר את הגרסה האנגלית"}
              </button>
            </div>
          </div>
        )}

        {/* "English version coming soon" notice for regular users in EN
            mode when the variant isn't approved yet. We still show the
            HE content below so they're not stuck staring at a blank. */}
        {pendingNote && !loading && (
          <div
            data-testid={`parasha-${mode}-pending-note`}
            className="mb-3 flex items-start gap-2 rounded-sm bg-gold/[0.06] border border-gold/25 px-3 py-2 text-xs text-muted-foreground"
            dir="ltr"
          >
            <Sparkles className="h-3.5 w-3.5 text-gold mt-0.5 flex-shrink-0" strokeWidth={1.5} />
            <span>
              English version coming soon — showing the Hebrew for now. Our rabbis review every English adaptation before it goes live.
            </span>
          </div>
        )}

        {loading && (
          <div className="grid place-items-center py-14 space-y-3" data-testid={`parasha-${mode}-loading`}>
            <Loader2 className="h-6 w-6 animate-spin text-gold" />
            <p className="text-xs text-muted-foreground">
              {drawLang === "en"
                ? (isStory ? "Composing a quality story, one moment…" : "Loading sources of Chazal…")
                : (isStory ? "מחבר סיפור איכותי, ייקח רגע…" : "טוען מקורות חז״ל…")}
            </p>
          </div>
        )}

        {!loading && errorKind === "missing" && (
          <div className="grid place-items-center py-12 text-center space-y-2" data-testid={`parasha-${mode}-missing`}>
            <Scroll className="h-6 w-6 text-gold/70" strokeWidth={1.25} />
            <p className="font-heading text-sm text-muted-foreground">
              {drawLang === "en"
                ? (isStory ? "A story for this parasha is coming soon" : "Sources of Chazal for this parasha are coming soon")
                : (isStory ? "סיפור לפרשה זו יתווסף בקרוב" : "מקורות חז״ל לפרשה זו יתווספו בקרוב")}
            </p>
          </div>
        )}

        {!loading && errorKind === "auth" && (
          <div className="grid place-items-center py-12 text-center space-y-3" data-testid={`parasha-${mode}-auth-required`}>
            <LogIn className="h-7 w-7 text-gold/80" strokeWidth={1.4} />
            <p className="font-heading text-base text-foreground">
              {isEn
                ? (isStory
                    ? "Sign in to read this week's parasha story"
                    : "Sign in to read this week's sources of Chazal")
                : (isStory
                    ? "התחבר כדי לקרוא את סיפור הפרשה השבועי"
                    : "התחבר כדי לקרוא את מקורות חז״ל לפרשה השבועית")}
            </p>
            <p className="text-xs text-muted-foreground max-w-xs leading-relaxed">
              {isEn
                ? "A free Torahya account opens the weekly parasha learning, the notebook, and your beit midrash dashboard."
                : "חשבון תוריא חינמי פותח את לימוד הפרשה השבועי, המחברת, ולוח בית המדרש שלך."}
            </p>
            <Button
              onClick={() => {
                onClose?.();
                navigate(`/auth?next=${encodeURIComponent("/")}`);
              }}
              data-testid={`parasha-${mode}-auth-signin-btn`}
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-heading min-h-[44px] px-6"
              size="sm"
            >
              {isEn ? "Sign in" : "התחברות"}
            </Button>
          </div>
        )}

        {!loading && errorKind === "fetch" && (
          <div className="grid place-items-center py-12 text-center space-y-2" data-testid={`parasha-${mode}-error`}>
            <p className="font-heading text-sm text-muted-foreground">
              {drawLang === "en"
                ? "Could not load right now. Please try again."
                : "לא הצלחנו לטעון כרגע. נסו שוב בעוד רגע."}
            </p>
          </div>
        )}

        {!loading && !errorKind && drawData && (
          <div className="space-y-6 mt-2" data-testid={`parasha-${mode}-content`}>
            {isStory ? (
              <StoryBody data={drawData} lang={drawLang} />
            ) : (
              <AggadahBody data={drawData} lang={drawLang} />
            )}

            {/* Action bar — save to notebook + share menu */}
            <div className="flex flex-col sm:flex-row gap-2 pt-3 border-t border-gold/15">
              {notebookPayload && (
                <SaveToNotebookButton
                  variant="chip"
                  className="flex-1 justify-center"
                  testIdSuffix={`parasha-${mode}`}
                  payload={notebookPayload}
                />
              )}
              <ShareMenu
                getImageNode={() => shareCardRef.current}
                getText={() => buildShareText(mode, slug, parashaHe, drawData, drawLang, t)}
                filename={`torahya-${mode}-${slug || "parasha"}`}
                title={
                  drawLang === "en"
                    ? (isStory ? `Story for Parashat ${slug || parashaHe}` : `From the Sources of Chazal · Parashat ${slug || parashaHe}`)
                    : (isStory ? `סיפור לפרשת ${parashaHe}` : `ממקורות חז״ל · פרשת ${parashaHe}`)
                }
                testIdSuffix={`parasha-${mode}`}
                variant="outline"
                buttonClassName="flex-1 justify-center border-gold/40 hover:border-gold hover:bg-gold/10 text-foreground rounded-sm"
                label={drawLang === "en" ? "Share with the family" : "שתפו עם המשפחה"}
                align="end"
              />
            </div>

            {/* Off-screen premium share card */}
            <div
              aria-hidden
              style={{ position: "fixed", left: -99999, top: 0, pointerEvents: "none", opacity: 0 }}
            >
              <ShareCard
                ref={shareCardRef}
                size="square"
                eyebrow={
                  drawLang === "en"
                    ? (isStory ? `Story for Parashat ${slug || parashaHe || ""}` : `From the Sources of Chazal · Parashat ${slug || parashaHe || ""}`)
                    : (isStory ? `סיפור לפרשת ${parashaHe || slug}` : `ממקורות חז״ל · פרשת ${parashaHe || slug}`)
                }
                title={
                  isStory
                    ? (drawLang === "en" ? (drawData.story_title_en || "") : (drawData.story_title_he || ""))
                    : (drawLang === "en" ? (drawData.title_en || "") : (drawData.title_he || ""))
                }
                body={
                  isStory
                    ? (drawLang === "en"
                        ? truncate(drawData.story_en || "", 500)
                        : truncate(drawData.story_he || "", 500))
                    : (drawLang === "en"
                        ? (drawData.paraphrase_en || drawData.source_text || "")
                        : (drawData.paraphrase_he || drawData.source_he_text || ""))
                }
                citation={
                  isStory ? "" : (drawLang === "en" ? (drawData.source_he || "") : (drawData.source_he || ""))
                }
                truncated={
                  isStory && (drawLang === "en"
                    ? (drawData.story_en || "").length > 500
                    : (drawData.story_he || "").length > 500)
                }
              />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Sub-components ─────────────────────────────────────────────

/** Story body — single flowing paragraph + lesson box. */
function StoryBody({ data, lang }) {
  const isEn = lang === "en";
  const storyText = isEn ? data.story_en : data.story_he;
  const lessonText = isEn ? data.story_lesson_en : data.story_lesson_he;
  const lessonHeading = data.is_authentic_lesson
    ? (isEn ? "Lesson from the aggadah" : "המסר מהאגדה")
    : (isEn ? "Lesson from the story" : "המסר העולה מהסיפור");
  return (
    <>
      <div
        data-testid="parasha-story-body"
        className="text-base sm:text-lg text-foreground leading-loose whitespace-pre-wrap break-words"
        style={{ lineHeight: "1.9" }}
      >
        {storyText}
      </div>
      {lessonText && (
        <section
          data-testid="parasha-story-lesson-section"
          className={`rounded-sm bg-foreground/[0.03] px-4 py-3 ${isEn ? "border-l-2 border-gold/60" : "border-r-2 border-gold/60"}`}
        >
          <p
            data-testid="parasha-story-lesson-heading"
            className="font-heading text-xs tracking-widest uppercase text-gold/85 mb-1.5"
          >
            {lessonHeading}
          </p>
          <p
            data-testid="parasha-story-lesson-text"
            className="text-sm sm:text-base text-foreground/90 leading-relaxed break-words"
          >
            {lessonText}
          </p>
        </section>
      )}
    </>
  );
}

/** Aggadah body — verbatim source + paraphrase + lesson. EN flows
 * present a fallback notice when the Sefaria source has no English. */
function AggadahBody({ data, lang }) {
  const isEn = lang === "en";
  const sourceText = isEn ? (data.source_text || data.source_he_text) : data.source_he_text;
  const sourceLangFallback = isEn && data.source_lang === "he_fallback";
  const paraphrase = isEn ? data.paraphrase_en : data.paraphrase_he;
  const lesson = isEn ? data.lesson_en : data.lesson_he;
  const sourceHeading = isEn ? "Words of the Midrash" : "לשון המדרש";
  const paraphraseHeading = isEn
    ? "Paraphrase — our explanation in modern English"
    : "פרפראזה — הסבר שלנו בעברית מודרנית";
  const lessonHeading = data.is_authentic_lesson
    ? (isEn ? "Lesson from the midrash" : "המסר מתוך המדרש")
    : (isEn ? "Lesson from the aggadah — our framing" : "המסר העולה מהאגדה — פרשנות שלנו");

  return (
    <>
      <section
        data-testid="aggadah-source-section"
        className="aggadah-source rounded-sm bg-gold/[0.04] border border-gold/15 px-5 py-4"
      >
        <header className={`flex items-center justify-between gap-2 mb-3 pb-2 border-b border-gold/15`}>
          <div className="flex items-center gap-2 text-gold/85">
            <Scroll className="h-3.5 w-3.5" strokeWidth={1.5} />
            <span className="font-heading text-xs tracking-widest uppercase">
              {sourceHeading}
            </span>
          </div>
          <span className="font-latin italic text-[11px] text-muted-foreground">
            {data.source_he}
          </span>
        </header>
        {sourceLangFallback && (
          <p
            data-testid="aggadah-source-fallback-note"
            className="mb-2 text-[11px] italic text-muted-foreground"
            dir="ltr"
          >
            English translation not yet available — showing the original Hebrew.
          </p>
        )}
        <p
          data-testid="aggadah-source-text"
          className="font-verse text-base sm:text-lg text-foreground leading-loose whitespace-pre-wrap break-words"
          dir={sourceLangFallback ? "rtl" : (isEn ? "ltr" : "rtl")}
          style={{ lineHeight: "2.0" }}
        >
          {sourceText}
        </p>
      </section>

      {paraphrase && (
        <section data-testid="aggadah-paraphrase-section" className="space-y-2">
          <div className="flex items-center gap-2 text-foreground/80">
            <span className="h-px w-6 bg-gold/40" aria-hidden="true" />
            <span className="font-heading text-xs tracking-widest uppercase">
              {paraphraseHeading}
            </span>
            <span className="h-px flex-1 bg-gold/40" aria-hidden="true" />
          </div>
          <p
            data-testid="aggadah-paraphrase-text"
            className="text-sm sm:text-base text-foreground/90 leading-relaxed break-words"
          >
            {paraphrase}
          </p>
        </section>
      )}

      {lesson && (
        <section
          data-testid="aggadah-lesson-section"
          className={`rounded-sm bg-foreground/[0.03] px-4 py-3 ${isEn ? "border-l-2 border-gold/60" : "border-r-2 border-gold/60"}`}
        >
          <p
            data-testid="aggadah-lesson-heading"
            className="font-heading text-xs tracking-widest uppercase text-gold/85 mb-1.5"
          >
            {lessonHeading}
          </p>
          <p
            data-testid="aggadah-lesson-text"
            className="text-sm sm:text-base text-foreground/90 leading-relaxed break-words"
          >
            {lesson}
          </p>
        </section>
      )}
    </>
  );
}

// ── Helpers ────────────────────────────────────────────────────

function truncate(s, n) {
  const t = String(s || "");
  return t.length <= n ? t : t.slice(0, n - 1).trim() + "…";
}

function buildShareText(mode, slug, parashaHe, data, lang) {
  const isStory = mode === "story";
  const isEn = lang === "en";
  const title = isStory
    ? (isEn ? data.story_title_en : data.story_title_he)
    : (isEn ? (data.title_en || data.title_he) : data.title_he);
  const body = isStory
    ? (isEn ? data.story_en : data.story_he)
    : (isEn
        ? (data.paraphrase_en || data.source_text)
        : (data.paraphrase_he || data.source_he_text));
  const lesson = isStory
    ? (isEn ? data.story_lesson_en : data.story_lesson_he)
    : (isEn ? data.lesson_en : data.lesson_he);
  const heading = isEn
    ? (isStory
        ? `Story for Parashat ${slug || parashaHe} — ${title}`
        : `From the Sources of Chazal · Parashat ${slug || parashaHe} — ${title}`)
    : (isStory
        ? `סיפור לפרשת ${parashaHe} — ${title}`
        : `ממקורות חז״ל · פרשת ${parashaHe} — ${title}`);
  const footer = isEn ? "✦ Torahya — your Beit Midrash" : "✦ תוריא — בית המדרש שלך";
  return [heading, "", body, "", lesson, "", footer].filter(Boolean).join("\n");
}

function buildNotebookPayload(mode, slug, parashaHe, data, lang) {
  const isStory = mode === "story";
  const isEn = lang === "en";
  const title = isStory
    ? (isEn ? data.story_title_en : data.story_title_he)
    : (isEn ? (data.title_en || data.title_he) : data.title_he);

  let content = "";
  if (isStory) {
    const story = isEn ? data.story_en : data.story_he;
    const lesson = isEn ? data.story_lesson_en : data.story_lesson_he;
    content = `${story || ""}\n\n${lesson || ""}`.trim();
  } else {
    const source = isEn ? (data.source_text || data.source_he_text) : data.source_he_text;
    const paraphrase = isEn ? data.paraphrase_en : data.paraphrase_he;
    const lesson = isEn ? data.lesson_en : data.lesson_he;
    const parts = [
      source,
      paraphrase && (isEn
        ? `\n— Modern English explanation —\n${paraphrase}`
        : `\n— הסבר בעברית מודרנית —\n${paraphrase}`),
      lesson && (isEn ? `\n— Lesson —\n${lesson}` : `\n— המסר —\n${lesson}`),
    ].filter(Boolean);
    content = parts.join("\n");
  }
  // Keep `content_he` as the field name — that's the existing notebook
  // schema. The notebook renderer treats it as the body regardless of
  // language; the source_label tells the user what to expect.
  const labelPrefix = isEn
    ? (isStory ? "Story" : "From the Sources of Chazal")
    : (isStory ? "סיפור" : "ממקורות חז״ל");
  const parashaLabel = isEn
    ? (slug || parashaHe || "")
    : (parashaHe || slug || "");
  return {
    source_type:  "ai",
    source_label: `${labelPrefix} — ${isEn ? "Parashat" : "פרשת"} ${parashaLabel}${title ? ` · ${title}` : ""}`,
    source_ref_he: title || "",
    content_he:    content,
  };
}
